/**
 * Escape closes the window when nothing in the page wanted the key (见 08).
 *
 * "Close" is deliberately the *same* action the title bar's close button takes,
 * not a second way to make a window disappear: the main window therefore still
 * obeys `workbench.window.closeToTray` (hide to the tray) while a detached or
 * shell window really closes. The desktop convention this follows — WeChat, QQ,
 * Telegram — is exactly that pairing, and reimplementing "hide" here would
 * fork it.
 *
 * **Why the renderer and not `before-input-event`**: the main process sees the
 * key *before* the page does, so a window with an open dialog would be closed
 * out from under it. The arbitration this feature needs only exists in the DOM.
 */

/** The parts of a `keydown` {@link shouldCloseWindowOnEscape} judges. */
export interface EscapeKeyPress {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
  readonly metaKey: boolean;
  /** `KeyboardEvent.repeat` — a held key. */
  readonly repeat: boolean;
  /** `KeyboardEvent.isComposing` — an IME composition is in progress. */
  readonly isComposing: boolean;
  /** Whether anything between the target and here already consumed the key. */
  readonly defaultPrevented: boolean;
}

/**
 * Whether this key press is a bare, unclaimed Escape that should close the
 * window. Called from a **bubble-phase listener on `window`**, the last node of
 * a `keydown`. Overlay dismiss (document) and every element handler have already
 * had their turn; other `window` listeners registered later — the keybinding
 * dispatcher, mounted by `KeybindingHost` — have not, so
 * {@link installEscapeToCloseWindow} re-checks after they finish. The four
 * rejections below rely on that ordering:
 *
 * - **`defaultPrevented`** is the whole arbitration mechanism, not a courtesy
 *   check. `@hibro/ui-client`'s overlays sit on Base UI, whose dismiss handler
 *   listens on `document` (bubble) and calls `preventDefault()` when Escape
 *   actually closes a layer — so every dialog, drawer, popover, menu, select and
 *   the command palette gets Escape first, with no list of them kept here. The
 *   same contract is what module code handling Escape must honour (见 08).
 * - **Modifiers** make it a different chord; only a bare Escape is this gesture.
 * - **`isComposing`** — Escape mid-composition cancels the IME candidate window,
 *   and that key press never belonged to the app at all.
 * - **`repeat`** — holding Escape must not queue a second close at the moment a
 *   window is being torn down.
 */
export function shouldCloseWindowOnEscape(press: EscapeKeyPress): boolean {
  return (
    press.key === 'Escape' &&
    !press.ctrlKey &&
    !press.shiftKey &&
    !press.altKey &&
    !press.metaKey &&
    !press.repeat &&
    !press.isComposing &&
    !press.defaultPrevented
  );
}

export interface EscapeCloseOptions {
  /** Close this window — `WindowControlsBridge.close`, the title bar's action. */
  close: () => void;
  /**
   * Whether the user currently has `workbench.window.escapeCloses` on. Read per
   * press rather than captured at install time, so toggling the setting takes
   * effect immediately and across every window without re-installing anything.
   */
  isEnabled: () => boolean;
  target?: Pick<Window, 'addEventListener' | 'removeEventListener'>;
}

/**
 * Install the Escape-closes-window handler on this window; returns a disposer.
 *
 * Two things Escape cannot reach, by construction rather than by omission: a
 * focused `WebContentsView` (the built-in browser's page content, 见 22) and a
 * focused module `iframe` both receive the key in their own realm, and a
 * cross-origin `keydown` does not propagate out to this one. Pressing Escape
 * with the page body focused works everywhere else.
 */
export function installEscapeToCloseWindow(
  options: EscapeCloseOptions,
): () => void {
  const target = options.target ?? window;
  let disposed = false;
  const handler = (event: KeyboardEvent): void => {
    if (disposed || !options.isEnabled() || !shouldCloseWindowOnEscape(event)) {
      return;
    }
    // This installer runs inside `bootShell` itself and therefore registers
    // *before* the keybinding dispatcher, which `KeybindingHost` installs from
    // a mount effect onto the same `window` bubble target.
    // The event is still being dispatched; wait until those later listeners
    // have had a chance to `preventDefault()` before treating the key as
    // unclaimed.
    queueMicrotask(() => {
      if (
        disposed ||
        !options.isEnabled() ||
        !shouldCloseWindowOnEscape(event)
      ) {
        return;
      }
      options.close();
    });
  };
  target.addEventListener('keydown', handler as EventListener);
  return () => {
    disposed = true;
    target.removeEventListener('keydown', handler as EventListener);
  };
}
